<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">

      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b">
        <h3 class="text-lg font-semibold text-gray-800">
          Assign Analytes — {{ service?.name }}
        </h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Search -->
      <div class="px-6 pt-4">
        <input
          v-model="search"
          type="text"
          placeholder="Search analytes..."
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <p class="text-xs text-gray-500 mt-1">
          {{ selectedIds.size }} of {{ allAnalytes.length }} selected
        </p>
      </div>

      <!-- Analyte list grouped by discipline -->
      <div class="flex-1 overflow-y-auto px-6 py-3">
        <div v-if="loading" class="text-center py-8 text-gray-400">Loading analytes...</div>
        <template v-else>
          <div v-for="(group, discipline) in groupedAnalytes" :key="discipline" class="mb-4">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {{ discipline || 'General' }}
            </p>
            <div class="space-y-1">
              <label
                v-for="analyte in group"
                :key="analyte.id"
                class="flex items-center gap-3 px-3 py-2 rounded cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  :value="analyte.id"
                  :checked="selectedIds.has(analyte.id)"
                  @change="toggleAnalyte(analyte.id)"
                  class="w-4 h-4 text-blue-600 rounded"
                />
                <span class="text-sm text-gray-800">{{ analyte.name }}</span>
                <span class="text-xs text-gray-400 ml-auto">{{ analyte.unit }}</span>
                <span class="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{{ analyte.result_type }}</span>
              </label>
            </div>
          </div>
          <p v-if="Object.keys(groupedAnalytes).length === 0" class="text-center text-gray-400 py-6">
            No analytes match your search.
          </p>
        </template>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 px-6 py-4 border-t">
        <button @click="$emit('close')" class="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50">
          Cancel
        </button>
        <button
          @click="save"
          :disabled="saving"
          class="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {{ saving ? 'Saving...' : 'Save Analytes' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/services/api'

export default {
  name: 'AssignAnalytesModal',
  props: {
    show:    { type: Boolean, required: true },
    service: { type: Object, default: null }
  },
  emits: ['close', 'saved'],
  data() {
    return {
      loading: false,
      saving:  false,
      search:  '',
      allAnalytes:  [],
      selectedIds:  new Set()
    }
  },
  computed: {
    filteredAnalytes() {
      const q = this.search.toLowerCase()
      if (!q) return this.allAnalytes
      return this.allAnalytes.filter(a => a.name.toLowerCase().includes(q))
    },
    groupedAnalytes() {
      return this.filteredAnalytes.reduce((acc, a) => {
        const key = a.discipline || ''
        if (!acc[key]) acc[key] = []
        acc[key].push(a)
        return acc
      }, {})
    }
  },
  watch: {
    show(val) {
      if (val && this.service) this.load()
    }
  },
  methods: {
    async load() {
      this.loading = true
      this.search  = ''
      try {
        const [allRes, linkedRes] = await Promise.all([
          apiClient.listLabAnalytes(this.service?.discipline ? { discipline: this.service.discipline } : {}),
          apiClient.fetchAnalytesForService(this.service.id)
        ])
        this.allAnalytes = allRes.success ? allRes.data : []
        const linked     = linkedRes.success ? linkedRes.data : []
        this.selectedIds = new Set(linked.map(a => a.id))
      } catch (e) {
        this.$toast.error('Failed to load analytes')
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    toggleAnalyte(id) {
      const next = new Set(this.selectedIds)
      next.has(id) ? next.delete(id) : next.add(id)
      this.selectedIds = next
    },
    async save() {
      this.saving = true
      try {
        const res = await apiClient.updateServiceAnalytes(
          this.service.id,
          [...this.selectedIds]
        )
        if (res.success) {
          this.$toast.success('Analytes saved successfully')
          this.$emit('saved')
          this.$emit('close')
        } else {
          this.$toast.error(res.message || 'Failed to save analytes')
        }
      } catch (e) {
        this.$toast.error('Failed to save analytes')
        console.error(e)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
